import { useCallback, useEffect, useState } from "react";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { zodResolver } from "@hookform/resolvers/zod";
import type { BunkerBargeDetailsPayload } from "@services/vessel/types";
import { updateBunkerBarge } from "@services/vesselsApi";
import { normalizeDate } from "@utils/date-time";
import { toDate, toISODateStrict } from "@utils/formatters/form";
import { paths } from "@utils/navigation";
import type { TFunction } from "i18next";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import { useUploadBunkerBargeDocuments } from "../useUploadBunkerBargeDocuments";

type Props = {
  vesselId: string;
  version: number;
  isOpen: boolean;
  onSuccess: () => void;
  onClose: () => void;
  endDate?: string | Date | null;
  remarks?: string;
};

const FORM_FIELDS = {
  END_DATE: "endDate",
  remarks: "remarks",
  CANCEL_BUNKER_BARGE_ATTACHMENTS: "cancelBunkerBargeAttachments",
} as const;

const LANG_BASE = "vessel-management.bunker-barge-page";
const TEST_ID_BASE = "cancel-bunker-barge-side-drawer";
const TASK_STATUS = "CANCEL_BB";
const MAX_ATTACHMENTS = 10;
const ATTACHMENT_DOC_TYPE = "DOCUMENT";

const getCancelBunkerBargeSchema = (t: TFunction, initialEndDate: string | Date | null) =>
  z
    .object({
      [FORM_FIELDS.END_DATE]: z
        .union([z.date(), z.string(), z.null()])
        .transform((val) => toDate(val)),
      [FORM_FIELDS.remarks]: z
        .string()
        .max(
          1500,
          t("form-validation.max-length", {
            label: t(`${LANG_BASE}.comments`),
            max: 1500,
          }),
        )
        .optional(),
      [FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS]: z
        .array(
          z.object({
            name: z.string(),
            url: z.string(),
            documentType: z.string().optional(),
          }),
        )
        .optional(),
    })
    .superRefine((data, ctx) => {
      const endDate = normalizeDate(data.endDate);
      const today = normalizeDate(new Date()) as Date;
      const endLabel = t(`${LANG_BASE}.end-date`);
      const initialEnd = normalizeDate(initialEndDate);

      if (!endDate) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.END_DATE],
          message: t("form-validation.required", { label: endLabel }),
        });
      }
      if (endDate && endDate < today) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.END_DATE],
          message: t("form-validation.must-not-be-in-past", { label: endLabel }),
        });
      }
      if (endDate && initialEnd && endDate >= initialEnd) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [FORM_FIELDS.END_DATE],
          message: t("form-validation.must-be-before", {
            subject: endLabel,
            object: t(`${LANG_BASE}.current-end-date`),
          }),
        });
      }
    });

const CancelBunkerBargeSideDrawer = ({
  vesselId,
  version,
  isOpen,
  onClose,
  endDate,
  remarks,
}: Props) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const navigate = useNavigate();
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [pendingPayload, setPendingPayload] = useState<BunkerBargeDetailsPayload | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();
  const { clearFilesForKey, setFilesForKey, filesByKey } = useUploadStore();

  const files = filesByKey[FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS] || [];

  const uploadConditions = FILE_UPLOAD_PROFILES.sideDrawerApprovalFile;
  const allowedSideDrawerExtensions = uploadConditions.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const form = useForm({
    mode: "onTouched",
    reValidateMode: "onChange",
    resolver: zodResolver(getCancelBunkerBargeSchema(t, endDate!)),
    shouldUnregister: true,
    defaultValues: {
      [FORM_FIELDS.END_DATE]: endDate ?? null,
      [FORM_FIELDS.remarks]: remarks ?? "",
      [FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS]: [],
    },
  });

  const handleUploadSuccess = useCallback(
    ({
      documentUrls,
      startIdx,
      files,
    }: {
      documentUrls: string[];
      startIdx: number;
      files: File[];
    }) => {
      const currentDocuments = form.getValues(FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS) ?? [];
      const nextDocuments = [...currentDocuments];

      documentUrls.forEach((url, fileOffset) => {
        const at = startIdx + fileOffset;
        const uploadedFile = files[fileOffset];

        nextDocuments[at] = {
          name: uploadedFile?.name ?? `document-${at + 1}`,
          url,
          documentType: uploadedFile?.type,
        };
      });

      const normalizedDocuments = nextDocuments
        .filter((document) => !!document?.url)
        .slice(-MAX_ATTACHMENTS);

      form.setValue(FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS, normalizedDocuments, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      setFilesForKey(FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS, normalizedDocuments, {
        mode: "replace",
        maxFiles: MAX_ATTACHMENTS,
      });
    },
    [form, setFilesForKey],
  );

  const uploadMutation = useUploadBunkerBargeDocuments({
    handleUploadSuccess,
    handleUploadError: (error) => {
      notify({
        title: t("vessel-management.notifications.attachments-upload-error"),
        description: t("vessel-management.notifications.attachments-upload-error-description"),
        type: "error",
      });
      console.error("Error on upload cancel bunker barge attachments:", error);
    },
  });

  const handleClose = () => {
    setIsCancelDialogOpen(false);
    form.reset();
    onClose();
    clearFilesForKey(FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS);
  };

  const handleSuccess = () => {
    handleClose();
    navigate(`/${paths.vesselManagement.index}`);
  };

  useEffect(() => {
    form.reset({
      [FORM_FIELDS.END_DATE]: endDate ?? null,
    });
    clearFilesForKey(FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS);
  }, [isOpen, endDate, form]);

  const onValid = async ({
    endDate,
    remarks,
    cancelBunkerBargeAttachments,
  }: {
    endDate?: Date | null;
    remarks?: string;
    cancelBunkerBargeAttachments?: { name: string; url: string; documentType?: string }[];
  }) => {
    if (uploadMutation.isPending) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }
    setPendingPayload({
      bunkerBargeId: vesselId,
      action: TASK_STATUS,
      version: version,
      effectiveEndDate: endDate ? toISODateStrict(endDate) : "",
      remarks: remarks,
      additionalDocuments: (cancelBunkerBargeAttachments ?? []).map((document) => ({
        documentType: ATTACHMENT_DOC_TYPE,
        documentUrl: document.url,
        fileName: document.name,
      })),
    });
    onClose();
    setIsCancelDialogOpen(true);
  };

  const handleConfirmCancel = async () => {
    if (!pendingPayload) return;
    try {
      setIsSubmitting(true);
      await updateBunkerBarge({
        service: "bff-vessel",
        id: vesselId,
        bunkerBargePayload: {
          ...pendingPayload,
        },
      });
    } catch (error: any) {
      notify({
        title: t("vessel-management.notifications.vessel-submitted-error-title"),
        description: error.response?.data?.detail ?? error.message,
        type: "error",
      });
      return;
    } finally {
      setIsSubmitting(false);
    }

    handleSuccess();
  };

  const onSubmit = () => form.handleSubmit(onValid)();

  const handleFileUpload = useCallback(
    (incomingFiles: File[]) => {
      if (!incomingFiles.length) return;

      const currentDocuments = form.getValues(FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS) ?? [];

      uploadMutation.mutate({
        files: incomingFiles,
        startIdx: currentDocuments.length,
        certificateType: "INTERNAL_NOTES",
        storeKey: FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS,
      });
    },
    [form, uploadMutation],
  );

  const handleRemoveFile = useCallback(
    (index: number) => {
      const currentDocuments = form.getValues(FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS) ?? [];
      const nextDocuments = currentDocuments.filter((_document, idx) => idx !== index);

      form.setValue(FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS, nextDocuments, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });

      setFilesForKey(FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS, nextDocuments, {
        mode: "replace",
        maxFiles: MAX_ATTACHMENTS,
      });
    },
    [form, setFilesForKey],
  );

  return (
    <>
      <FormProvider {...form}>
        <SideDrawer
          isOpen={isOpen}
          onClose={handleClose}
          onSubmit={onSubmit}
          keepMounted={false}
          title={t(`${LANG_BASE}.cancelTitle`)}
          description={t(`${LANG_BASE}.cancel-bunker-barge-description`)}
          submitLabel={t(`${LANG_BASE}.proceed`)}
          width={400}
        >
          <div className="w-[400px] p-6">
            <FormItem
              name={FORM_FIELDS.END_DATE}
              type="date-picker"
              label={t(`${LANG_BASE}.end-date`)}
              testId={`${TEST_ID_BASE}-to-date`}
              timePicker={false}
              maxDate={
                endDate ? new Date(new Date(endDate).getTime() - 24 * 60 * 60 * 1000) : undefined
              }
              dateConstraint="not-past"
              required={true}
            />
            <FormItem
              name={FORM_FIELDS.remarks}
              type="textarea"
              label={t(`${LANG_BASE}.comments`)}
              testId={`${TEST_ID_BASE}-comments`}
              minRows={4}
              maxChars={1500}
              showCharCounter
            />
            <div className="flex flex-col gap-2">
              <DocumentUpload
                name={FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS}
                label={t(`${LANG_BASE}.cancel-Attachments`)}
                testId={`${TEST_ID_BASE}-cancel-bunker-barge-attachments`}
                allowedExtensions={allowedSideDrawerExtensions}
                conditions={uploadConditions}
                maxFiles={MAX_ATTACHMENTS}
                onUpload={handleFileUpload}
                setFiles={(next) =>
                  setFilesForKey(
                    FORM_FIELDS.CANCEL_BUNKER_BARGE_ATTACHMENTS,
                    next as UploadEntry[],
                    {
                      mode: "replace",
                      maxFiles: MAX_ATTACHMENTS,
                    },
                  )
                }
                files={files}
                disabled={uploadMutation.isPending || files.length >= MAX_ATTACHMENTS}
              />
              {files.map((file, index) => (
                <SingleFile
                  testId={`${TEST_ID_BASE}-attachment-${index}`}
                  file={file}
                  fieldName={file.name}
                  currentFileIndex={index}
                  setFiles={() => handleRemoveFile(index)}
                  isLoading={uploadMutation.isPending && file instanceof File}
                />
              ))}
            </div>
          </div>
        </SideDrawer>
      </FormProvider>
      <GenericDialog
        open={isCancelDialogOpen}
        isSubmitting={isSubmitting}
        handleCloseDialog={() => setIsCancelDialogOpen(false)}
        confirmAction={() => handleConfirmCancel()}
        title={t(`${LANG_BASE}.cancel-dialog.title`)}
        content={t(`${LANG_BASE}.cancel-dialog.description`)}
        cancelButtonText={t(`${LANG_BASE}.cancel-dialog.cancel-button`)}
        confirmButtonText={t(`${LANG_BASE}.cancel-dialog.confirm-button`)}
      />
    </>
  );
};

export default CancelBunkerBargeSideDrawer;
